import { useDisclosure } from '@mantine/hooks'
import { ArrowDownward, ArrowUpward } from '@mui/icons-material'
import {
  Avatar,
  Box,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { isEmpty } from 'lodash'
import { Link } from 'react-router-dom'

import libbySrc from '~/assets/images/about/libby.webp'
import logoDarkSrc from '~/assets/images/corp/logo-dark.webp'
import Links from '~/features/chatbot/components/simple/header/links'
import SocialLinks from '~/features/chatbot/components/socialLinks'
import { partializeLinks } from '~/features/chatbot/components/socialLinks/utils'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import { EChatbotTheme } from '~/features/settings/models'
import useGetElementDimensions from '~/features/ui/hooks/useGetElementDimensions'
import useIdentifyViewportWidth from '~/features/ui/hooks/useIdentifyViewportWidth'
import EElement from '~/features/ui/models'
import { EMarketingClientPath } from '~/models/api'
import { store } from '~/store'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

function Header() {
  const theme = useTheme()
  const isPartner = useIsPartner()
  const { isExtraSmallScreen, isSmallScreen } = useIdentifyViewportWidth()
  const [open, { toggle }] = useDisclosure(false)
  const { height: headerHeight } = useGetElementDimensions(
    EElement.chatbotHeader,
  )

  const [chatbotLayout, chatbotAgent, referralCode] = store((state) => [
    state.chatbot.layout,
    state.chatbot.agent,
    state.organization.referralCode,
  ])

  const [socialLinks, agentLinks] = partializeLinks(
    chatbotAgent?.customLinks ?? [],
  )

  return (
    <Stack
      id={EElement.chatbotHeader}
      sx={{
        alignItems: { xxxs: 'flex-start', sm: 'center' },
        justifyContent: 'flex-start',
        width: { xxxs: '100%', md: '30%' },
        minHeight: { xxxs: !isEmpty(agentLinks) ? 100 : 60, sm: 140, md: 300 },
        height: { xxxs: 'auto', md: '100%' },
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.common.white,
        ...(isSmallScreen || isExtraSmallScreen ? { position: 'fixed' } : {}),
      }}
    >
      <Stack
        sx={{
          marginTop: { xxxs: 1, xs: 2 },
          width: { xxxs: '100%', md: 300 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: { xxxs: 'row', md: 'column' },
            gap: { xxxs: 2, md: 1 },
          }}
        >
          {chatbotAgent.showBranding && !isPartner && (
            <MuiLink
              component={Link}
              target="blank"
              to={
                referralCode
                  ? pathBuilder(EMarketingClientPath.referralLink, {
                      referralCode,
                    })
                  : EMarketingClientPath.home
              }
              sx={{
                display: { xxxs: 'none', md: 'block' },
              }}
            >
              <Box
                height={32}
                width={100}
                sx={{
                  backgroundImage: `url(${logoDarkSrc})`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            </MuiLink>
          )}
          <Box
            sx={{
              borderRadius: '50%',
              border: `${rem(3)} solid ${chatbotLayout.selection[EChatbotTheme.simple]}`,
              padding: rem(3),
            }}
          >
            <Avatar
              src={chatbotAgent.chatbotImgURL ?? libbySrc}
              sx={{
                width: { xxxs: 32, sm: 72, md: 96 },
                height: { xxxs: 32, sm: 72, md: 96 },
                transition: 'all 1s ease-in-out',
              }}
            />
          </Box>

          <Stack
            sx={{
              alignItems: { xxxs: 'flex-start', sm: 'center' },
              width: 'fit-content',
              maxWidth: rem(210),
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: { xxxs: rem(16), sm: rem(20) },
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.mainFont,
                lineHeight: 1.4,
              }}
            >
              {chatbotAgent.businessName}
            </Typography>
            <Typography
              sx={{
                display: { xxxs: 'none', md: 'block' },
                fontSize: { xxxs: rem(13), sm: rem(15) },
                fontWeight: theme.typography.fontWeightMedium,
                color: theme.palette.mainFont,
                lineHeight: 1.4,
                marginBottom: 1,
              }}
            >
              {chatbotAgent.service}
            </Typography>
            <Stack
              sx={{
                maxWidth: {
                  xxxs: 200,
                  md: '100%',
                },
                gap: 1,
                marginBottom: { xxxs: 0, md: 2 },
              }}
            >
              <Typography
                sx={{
                  color: chatbotLayout.selection[EChatbotTheme.simple],
                  typography: { xxxs: 'caption', md: 'body2' },
                }}
              >
                Talk to {chatbotAgent.agentName}, my AI assistant.
              </Typography>
              {!isEmpty(socialLinks) && <SocialLinks links={socialLinks} />}
            </Stack>
          </Stack>
        </Box>
        {!isEmpty(agentLinks) && (
          <IconButton
            onClick={toggle}
            sx={{
              alignSelf: 'center',
              display: {
                xxxs: 'block',
                md: 'none',
              },
            }}
          >
            {open ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        )}
      </Stack>
      {!isEmpty(agentLinks) && (
        <Box
          sx={{
            maxHeight: '100vh',
            overflowY: 'auto',
            transition: 'all 0.3s ease-in-out',
            position: { xxxs: 'absolute', md: 'relative' },
            top: { xxxs: '100%', md: 0 },
            width: '100%',
            backgroundColor: theme.palette.common.white,
            paddingInline: 1,
            height: {
              xxxs: open ? `calc(100vh - ${rem(headerHeight)})` : 0,
              md: '100%',
            },
          }}
        >
          <Links links={agentLinks} />
        </Box>
      )}
    </Stack>
  )
}

export default Header
